import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import { format, isFuture, subDays } from 'date-fns';
import { Chip } from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import InstantPayStatusChip from '@/enterprise/components/InstantPay/InstantPayStatusChip';
import { fetchRecentClearsExistenceProbe } from '@/enterprise/data/useRecentClearsProbe';
import { fetchTakerHistory, SupplierMarketHistory } from '@/enterprise/data/useTakerHistory';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import BuyerLogo from '../BuyerLogo';
import Widget, { WidgetSkeleton } from './Widget';

const EARLY_PAY_HISTORY_WIDGET_LIMIT = 5;

const useEarlyPayHistory = () => {
  const { selectedCurrency } = useCurrencyContext();
  const startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const recentHistory = useSuspenseQuery({
    queryKey: ['early-pay-history-widget', selectedCurrency],
    queryFn: () =>
      fetchTakerHistory({
        currency: selectedCurrency,
        endDate: '',
        limit: EARLY_PAY_HISTORY_WIDGET_LIMIT,
        page: 1,
        sort: '-depositDate',
        startDate,
      }),
  });

  const hasNoRecentHistory = recentHistory.data.length === 0;

  const allTimeHistory = useSuspenseQuery({
    queryKey: ['early-pay-history-widget-all-time', selectedCurrency],
    queryFn: () => (hasNoRecentHistory ? fetchRecentClearsExistenceProbe() : []),
  });

  return {
    data: recentHistory.data as SupplierMarketHistory[],
    hasHistoryOutsideRange: hasNoRecentHistory && (allTimeHistory.data?.length ?? 0) > 0,
  };
};

const EarlyPayHistoryWidgetComponent = () => {
  const { t } = useTranslation();
  const { data: takerMarkets } = useTakerMarkets();
  const { data: earlyPayHistory, hasHistoryOutsideRange } = useEarlyPayHistory();
  const { asCurrency, asDateStringText } = useLocaleFormat();

  const itemMap = earlyPayHistory
    .map((item) => {
      const takerMarket = takerMarkets.find(
        (tm) =>
          tm.makerOrganizationUuid === item.supplierMarket.makerOrganizationUuid &&
          tm.takerDivisionUuid === item.supplierMarket.supplierDivisionUuid &&
          tm.marketUuid === item.supplierMarket.marketUuid
      );

      if (!takerMarket) {
        return null;
      }

      return {
        history: item,
        takerMarket,
      };
    })
    .filter((item) => item !== null);

  const items = itemMap.map((item) => {
    return {
      key: item.history.uuid,
      content: (
        <div className="flex items-center gap-2">
          <div>
            <BuyerLogo
              buyerLogoUrl={item.takerMarket.makerOrganizationLogo}
              buyerName={item.takerMarket.makerOrganizationName}
              size="lg"
            />
          </div>
          <div className="min-w-0 truncate">
            <div className="truncate">{item.takerMarket.makerOrganizationName}</div>
            <div className="truncate font-serif text-2xl">
              {asCurrency(item.history.clearedDiscountedAmount, item.history.currency)}
            </div>
            <div className="truncate text-sm text-text-secondary">
              {t('draftOffer.labels.estimatedDepositDate')}:{' '}
              {item.history.depositDate ? asDateStringText({ date: item.history.depositDate, isUTC: false }) : '--'}
            </div>
            <div className="mt-1">
              {item.history.isInstantPay ? (
                <InstantPayStatusChip withTooltip={false} />
              ) : (
                <Chip
                  size="xs"
                  type={item.history.isPendingClear ? 'default' : 'primary'}
                  variant="outlined"
                  isRound
                  label={
                    item.history.isPendingClear ? t('core.history.statusPending') : t('core.history.statusAwarded')
                  }
                />
              )}
            </div>
          </div>
        </div>
      ),
      link: asEnterpriseLink(`history?includeFuturePayDates=true&makerIds%5B0%5D=${item.takerMarket.makerDivisionId}`),
    };
  });

  const subtitle = `${t('core.nextEstimatedDepositDate')}: ${
    itemMap[0]?.history.depositDate && isFuture(new Date(itemMap[0].history.depositDate))
      ? asDateStringText({ date: itemMap[0].history.depositDate, isUTC: false })
      : '--'
  }`;

  return (
    <Widget
      items={items}
      subtitle={subtitle}
      title={t('core.earlyPayHistory')}
      viewAllLink={asEnterpriseLink('history')}
      widgetType="early-pay-history"
      zeroItemsMessage={t(
        hasHistoryOutsideRange
          ? 'homepage.earlyPayHistoryWidgetZeroItemsInRangeMessage'
          : 'homepage.earlyPayHistoryWidgetZeroItemsMessage'
      )}
      showViewAllWhenEmpty={hasHistoryOutsideRange}
    />
  );
};

const EarlyPayHistoryWidget = () => (
  <QueryBoundaries LoadingComponent={() => <WidgetSkeleton />}>
    <EarlyPayHistoryWidgetComponent />
  </QueryBoundaries>
);

export default EarlyPayHistoryWidget;
